import type {
  PortalClubwiseBlock,
  PortalClubwiseBreakdownRow,
  PortalClubwiseSalesRow,
  PortalClubwiseStatusRow,
} from '../../../../../types/portalReport';
import { JOINED_REPEAT_NOTE } from '../../../../../lib/clubwise/report';
import { monthName, shortDay } from '../../../../../lib/clubwise/window';
import { Band, SectionHeading } from './controls';
import { ClubwiseChannelTable } from './ClubwiseChannelTable';
import { DASH, numberFull, percent } from './format';

// CLIENT PORTAL — the "ClubWise data" tab (Anytime Fitness).
//
// ⚠️⚠️ AGGREGATES ONLY. Every table is counts and percentages; nothing here can show
// a member, and no member-level row exists in the payload to show.
//
// ⚠️ Every figure covers the WHOLE UPLOADED MONTHS named at the top, and every
// "Joined" is a cohort figure (this period's leads who have joined so far), so it
// rises as later months are uploaded. A month nobody uploaded is "not measured",
// never zero.

const NOT_MEASURED = 'Not measured';

function monthsLine(months: readonly string[]): string {
  if (months.length === 0) return '';
  const sorted = [...months].sort();
  return sorted.length === 1
    ? monthName(sorted[0]!)
    : `${monthName(sorted[0]!)} to ${monthName(sorted[sorted.length - 1]!)}`;
}

function BreakdownTable({
  title,
  intro,
  rows,
  labelHeading,
}: {
  title: string;
  intro?: string;
  rows: PortalClubwiseBreakdownRow[];
  labelHeading: string;
}) {
  return (
    <Band>
      <SectionHeading title={title} intro={intro} />
      <table className="rpt-table rpt-channel-table" data-testid={`clubwise-${title}`}>
        <thead>
          <tr>
            <th scope="col">{labelHeading}</th>
            <th scope="col">Leads</th>
            <th scope="col">Joined</th>
            <th scope="col">Conversion</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.label}>
              <th scope="row">{r.label}</th>
              <td>{numberFull(r.leads)}</td>
              <td>{numberFull(r.joined)}</td>
              <td>{r.conversionRate === null ? DASH : percent(r.conversionRate)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <p className="rpt-sub rpt-muted">{JOINED_REPEAT_NOTE}</p>
    </Band>
  );
}

function SalesTable({ rows, labelHeading }: { rows: PortalClubwiseSalesRow[]; labelHeading: string }) {
  return (
    <table className="rpt-table rpt-channel-table">
      <thead>
        <tr>
          <th scope="col">{labelHeading}</th>
          <th scope="col">Sales</th>
          <th scope="col">From earlier leads</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => (
          <tr key={r.label}>
            <th scope="row">{r.label}</th>
            <td>{numberFull(r.sales)}</td>
            <td>{numberFull(r.fromEarlierLeads)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

const STATUS_NOTE: Record<PortalClubwiseStatusRow['kind'], string> = {
  joined: 'Joined',
  open: 'Still open',
  lost: 'Did not join',
  unknown: 'Status not recognised',
};

export function ClubwiseDataPage({ block }: { block: PortalClubwiseBlock }) {
  const detail = block.detail;
  if (block.measuredMonths.length === 0 || !detail) {
    return (
      <Band>
        <SectionHeading title="ClubWise data" />
        <p className="rpt-sub rpt-muted" role="note">
          No whole month of ClubWise data has been uploaded for this period yet, so there is nothing to show. A month that has
          not been uploaded is not measured; it is never shown as zero.
        </p>
      </Band>
    );
  }
  const q = detail.quality;
  const pctOf = (n: number): string => (q.leads > 0 ? percent(Math.round((n / q.leads) * 1000) / 10) : DASH);
  return (
    <>
      <Band>
        <SectionHeading
          title="ClubWise data"
          intro={`Covers ${monthsLine(block.measuredMonths)}: ${
            (block.monthToDate ?? []).length > 0
              ? `uploaded months. ${(block.monthToDate ?? []).map((x) => `${monthName(x.month).split(' ')[0]} is to ${shortDay(x.dataTo)} (latest upload)`).join(', ')}.`
              : 'whole months that have been uploaded.'
          } Joined counts this period's leads who have joined so far, so it rises as later months are uploaded.`}
        />
      </Band>

      <Band>
        <SectionHeading title="Channel type" />
        <ClubwiseChannelTable block={block} showBar={false} />
      </Band>

      <BreakdownTable title="Contact type" rows={detail.contactTypes} labelHeading="Contact type" />
      <BreakdownTable
        title="Lead source"
        intro="A digital enquiry with no source recorded appears as its own row."
        rows={detail.sources}
        labelHeading="Source"
      />

      <Band>
        <SectionHeading
          title="Sales by source"
          intro="Every join in these months, including members whose lead was entered in an earlier month."
        />
        <p className="rpt-sub" data-testid="clubwise-sales-earlier">
          {`${numberFull(detail.sales)} sales, ${numberFull(detail.salesFromEarlierLeads)} of them from leads entered in earlier months.`}
        </p>
        <SalesTable rows={detail.salesBySource} labelHeading="Source" />
        <SalesTable rows={detail.salesByContactType} labelHeading="Contact type" />
      </Band>

      <Band>
        <SectionHeading title="Joins by membership type" />
        <table className="rpt-table rpt-channel-table">
          <thead>
            <tr>
              <th scope="col">Membership type</th>
              <th scope="col">Joins</th>
            </tr>
          </thead>
          <tbody>
            {detail.membershipTypes.map((r) => (
              <tr key={r.label}>
                <th scope="row">{r.label}</th>
                <td>{numberFull(r.count)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Band>

      <Band>
        <SectionHeading
          title="Lead status now"
          intro="Where this period's leads stand today. Hot Prospect and Prospect together are still open."
        />
        <p className="rpt-sub" data-testid="clubwise-still-open">{`Still open: ${numberFull(detail.stillOpen)} leads`}</p>
        <table className="rpt-table rpt-channel-table">
          <thead>
            <tr>
              <th scope="col">Status</th>
              <th scope="col">Leads</th>
              <th scope="col" />
            </tr>
          </thead>
          <tbody>
            {detail.statuses.map((r) => (
              <tr key={r.label}>
                <th scope="row">{r.label}</th>
                <td>{numberFull(r.count)}</td>
                <td className="rpt-muted">{STATUS_NOTE[r.kind]}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Band>

      <Band>
        <SectionHeading
          title="Data quality"
          intro="How complete the uploaded data is. Untagged leads cannot be credited to a source."
        />
        <table className="rpt-table rpt-channel-table" data-testid="clubwise-quality">
          <tbody>
            <tr>
              <th scope="row">Leads with no source</th>
              <td>{numberFull(q.noSource)}</td>
              <td>{pctOf(q.noSource)}</td>
            </tr>
            <tr>
              <th scope="row">Leads with no contact type</th>
              <td>{numberFull(q.noContactType)}</td>
              <td>{pctOf(q.noContactType)}</td>
            </tr>
            <tr>
              <th scope="row">Unrecognised statuses</th>
              <td>{numberFull(q.unknownStatusValues)}</td>
              <td>{`${numberFull(q.unknownStatusLeads)} leads`}</td>
            </tr>
          </tbody>
        </table>
      </Band>

      <Band>
        <SectionHeading
          title="12-month trends"
          intro="Leads and joins per channel type, by month. A month that has not been uploaded is not measured."
        />
        <div className="rpt-table-wrap">
          <table className="rpt-table rpt-channel-table" data-testid="clubwise-trends">
            <thead>
              <tr>
                <th scope="col">Month</th>
                {block.channels.map((c) => (
                  <th scope="col" key={c.channel}>
                    {c.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {detail.trends.map((m) => (
                <tr key={m.month}>
                  <th scope="row">{monthName(m.month)}</th>
                  {m.channels === null
                    ? block.channels.map((c) => (
                        <td key={c.channel} className="rpt-muted">
                          {NOT_MEASURED}
                        </td>
                      ))
                    : m.channels.map((c) => (
                        <td key={c.channel}>{`${numberFull(c.leads)} leads · ${numberFull(c.joined)} joined`}</td>
                      ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="rpt-sub rpt-muted">{JOINED_REPEAT_NOTE}</p>
      </Band>
    </>
  );
}
